import React from 'react';
import { CheckIcon } from '@heroicons/react/24/solid';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { ProductRangeTableProps } from './ProductRangeTypes';

type Matrix = ProductRangeTableProps['matrix'];
type CustomerId =
    ProductRangeTableProps['selectedCustomerObjects'][number]['id'];
type FamilyId =
    ProductRangeTableProps['visibleFamilies'][number]['id'];

interface ToggleButtonProps {
  checked: boolean;
  label: string;
  onToggle: () => void;
  partial?: boolean;
  disabled?: boolean;
  compact?: boolean;
  className?: string;
}

const isFamilyEnabled = (
    matrix: Matrix,
    customerId: CustomerId,
    familyId: FamilyId
) => matrix[customerId]?.includes(familyId) ?? false;

const getSelectionState = (enabled: number, total: number) => ({
  all: total > 0 && enabled === total,
  some: enabled > 0 && enabled < total,
});

const ToggleButton = ({checked, partial = false, disabled = false, compact = false, label, onToggle, className = ''}: ToggleButtonProps) => {
  const stateClass = checked ? 'border-emerald-600 bg-emerald-500 text-white shadow-sm' : partial ? 'border-amber-600 bg-amber-500 text-white shadow-sm' : 'border-slate-300 bg-white text-transparent hover:border-sky-500';

  return (
      <button type="button" onClick={onToggle} disabled={disabled} aria-label={label} aria-pressed={partial ? 'mixed' : checked} className={`inline-flex items-center justify-center rounded-sm border transition ${compact ? 'h-5 w-5' : 'h-6 w-6'} ${stateClass} disabled:cursor-not-allowed disabled:opacity-50 ${className}`}>
        <CheckIcon className={compact ? 'h-3 w-3' : 'h-3.5 w-3.5'} />
      </button>
  );
};

export const ProductRangeTable: React.FC<ProductRangeTableProps> = ({
  selectedCustomerObjects,
  visibleFamilies,
  groupedVisibleFamilies,
  expandedAreas,
  matrix,
  hasMultipleSelectedCustomers,
  allVisibleFamiliesEnabledForSelectedCustomers,
  someVisibleFamiliesEnabledForSelectedCustomers,
  toggleAreaExpansion,
  applyBulkChange,
  updateAllFamiliesForCustomer,
  updateAreaForCustomer,
  updateAreaForSelectedCustomers,
  updateFamilyForSelectedCustomers,
  updateCustomerFamily,
  templateMode,
  templateMatrix,
  allVisibleFamiliesEnabledForTemplate,
  someVisibleFamiliesEnabledForTemplate,
  updateAllFamiliesForTemplate,
  updateAreaForTemplate,
  updateTemplateFamily,
}) => {
const noVisibleFamilies = visibleFamilies.length === 0;
return (
  <div className="overflow-auto rounded-lg border border-gray-200">
    <table className="min-w-full border-separate border-spacing-0">
      <thead className="bg-gray-50 sticky top-0 z-10">
        <tr>
          <th className="sticky left-0 z-20 bg-gray-50 px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 min-w-[260px]">Product family</th>
          {!templateMode && hasMultipleSelectedCustomers && (
            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 min-w-[120px]">
              <div>All selected customers</div>
              <ToggleButton checked={allVisibleFamiliesEnabledForSelectedCustomers} partial={someVisibleFamiliesEnabledForSelectedCustomers} disabled={noVisibleFamilies} className="mt-2" 
                            label={allVisibleFamiliesEnabledForSelectedCustomers ? 'Disable all families for selected customers' : 'Enable all families for selected customers'} 
                            onToggle={() => applyBulkChange(!allVisibleFamiliesEnabledForSelectedCustomers)}
              />
            </th>
          )}
          {templateMode ? (
            <th className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 min-w-[180px]">
              <div>Template</div>
              <ToggleButton checked={allVisibleFamiliesEnabledForTemplate} partial={someVisibleFamiliesEnabledForTemplate} disabled={noVisibleFamilies} className="mt-2"
                            label={allVisibleFamiliesEnabledForTemplate ? 'Disable all families in template' : 'Enable all families in template'}
                            onToggle={() => updateAllFamiliesForTemplate(!allVisibleFamiliesEnabledForTemplate)}
              />
            </th>
          ) : selectedCustomerObjects.map((customer) => {
            const enabledCount = visibleFamilies.filter(family => isFamilyEnabled(matrix, customer.id, family.id)).length;
            const selection = getSelectionState(enabledCount, visibleFamilies.length);
            return (
              <th key={customer.id} className="px-4 py-3 text-center text-xs font-semibold uppercase tracking-wide text-gray-500 border-b border-gray-200 min-w-[180px]">
                <div>{customer.customerName}</div>
                <div className="normal-case text-[11px] font-normal text-gray-400">{customer.id} - {customer.siteId} - {customer.salesCompanyId}</div>
                <ToggleButton checked={selection.all} partial={selection.some} disabled={noVisibleFamilies} className="mt-2" 
                              label={selection.all ? `Disable all families for ${customer.customerName}` : `Enable all families for ${customer.customerName}`} 
                              onToggle={() => updateAllFamiliesForCustomer(customer.id, !selection.all)}
                />
              </th>
            );
          })}
        </tr>
      </thead>
      <tbody>
        {groupedVisibleFamilies.map((group) => {
          const isExpanded = expandedAreas[group.area] ?? false;
          const areaEnabledForAllCustomers = selectedCustomerObjects.every((customer) => group.families.every((family) => isFamilyEnabled(matrix, customer.id, family.id)));
          const areaEnabledForTemplate = group.families.every((family) => isFamilyEnabled(templateMatrix, 'template', family.id));
          return (
            <React.Fragment key={group.area}>
              <tr className="bg-slate-100">
                <td className="sticky left-0 z-10 bg-slate-100 px-4 py-3 border-b border-gray-200 align-top">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex min-w-0 items-start gap-3">
                      <button type="button" onClick={() => toggleAreaExpansion(group.area)} className="inline-flex items-center gap-2 text-left text-sm font-semibold text-gray-900 hover:text-sky-700">
                        {isExpanded ? <ChevronDownIcon className="h-4 w-4" /> : <ChevronRightIcon className="h-4 w-4" />}
                        <span>{group.area}</span>
                      </button>
                      <div className="flex flex-col items-start gap-1 pt-0.5">
                        <span className="text-xs font-normal text-gray-500">{group.families.length} product families</span>
                      </div>
                    </div>
                  </div>
                </td>
                {templateMode ? (
                  <td className="px-4 py-3 border-b border-gray-200 text-center bg-slate-100">
                    <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">
                      <ToggleButton checked={areaEnabledForTemplate} compact label={areaEnabledForTemplate ? `Disable ${group.area} in template` : `Enable ${group.area} in template`} onToggle={() => updateAreaForTemplate(group.area, !areaEnabledForTemplate)}/>
                      <span className="text-xs font-medium">{group.families.filter((family) => isFamilyEnabled(templateMatrix, 'template', family.id)).length}/{group.families.length}</span>
                    </div>
                  </td>
                ) : (
                  <>
                    {hasMultipleSelectedCustomers && (
                      <td className="px-4 py-3 border-b border-gray-200 text-center bg-slate-100">
                        <ToggleButton checked={areaEnabledForAllCustomers} label={areaEnabledForAllCustomers ? `Disable all ${group.area} families for selected customers` : `Enable all ${group.area} families for selected customers`} onToggle={() => updateAreaForSelectedCustomers(group.area, !areaEnabledForAllCustomers)}/>
                      </td>
                    )}
                    {selectedCustomerObjects.map((customer) => {
                  const enabledCount = group.families.filter(family => isFamilyEnabled(matrix, customer.id, family.id)).length;
                  const allEnabled = enabledCount === group.families.length;
                  return (
                    <td key={`${group.area}:${customer.id}`} className="px-4 py-3 border-b border-gray-200 text-center bg-slate-100">
                      <div className="inline-flex items-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-sm text-slate-700">
                        <ToggleButton checked={allEnabled} compact label={allEnabled ? `Disable ${group.area} for ${customer.customerName}` : `Enable ${group.area} for ${customer.customerName}`} onToggle={() => updateAreaForCustomer(customer.id, group.area, !allEnabled)}/>
                        <span className="text-xs font-medium">{enabledCount}/{group.families.length}</span>
                      </div>
                    </td>
                  );
                    })}
                  </>
                )}
              </tr>
              {isExpanded && group.families.map((family) => {
                const enabledForAllCustomers = selectedCustomerObjects.every(customer => isFamilyEnabled(matrix, customer.id, family.id));
                const enabledForTemplate = isFamilyEnabled(templateMatrix, 'template', family.id);
                return (
                  <tr key={family.id} className="odd:bg-white even:bg-slate-50/50">
                    <td className="sticky left-0 bg-inherit px-4 py-3 border-b border-gray-200 align-top">
                      <div className="pl-6">
                        <div className="font-medium text-gray-900">{family.name}</div>
                        <div className="text-xs text-gray-500">{family.area}</div>
                      </div>
                    </td>
                    {templateMode ? (
                      <td className="px-4 py-3 border-b border-gray-200 text-center">
                        <ToggleButton checked={enabledForTemplate} label={enabledForTemplate ? `Disable ${family.name} in template` : `Enable ${family.name} in template`} onToggle={() => updateTemplateFamily(family.id, !enabledForTemplate)}/>
                      </td>
                    ) : (
                      <>
                        {hasMultipleSelectedCustomers && (
                          <td className="px-4 py-3 border-b border-gray-200 text-center">
                            <ToggleButton checked={enabledForAllCustomers} label={enabledForAllCustomers ? `Disable ${family.name} for all selected customers` : `Enable ${family.name} for all selected customers`} onToggle={() => updateFamilyForSelectedCustomers(family.id, !enabledForAllCustomers)}/>
                          </td>
                        )}
                        {selectedCustomerObjects.map((customer) => {
                      const enabled = isFamilyEnabled(matrix, customer.id, family.id);
                      return (
                        <td key={`${family.id}:${customer.id}`} className="px-4 py-3 border-b border-gray-200 text-center">
                          <ToggleButton checked={enabled} label={enabled ? `Disable ${family.name} for ${customer.customerName}` : `Enable ${family.name} for ${customer.customerName}`} onToggle={() => updateCustomerFamily(customer.id, family.id, !enabled)}/>
                        </td>
                      );
                        })}
                      </>
                    )}
                  </tr>
                );
              })}
            </React.Fragment>
          );
        })}
        {noVisibleFamilies && (
          <tr>
            <td colSpan={templateMode ? 2 : selectedCustomerObjects.length + (hasMultipleSelectedCustomers ? 2 : 1)} className="px-4 py-10 text-center text-sm text-gray-500">
              No product families match the current filters.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
  );
};
